import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import Dropdown from "components/common/Dropdown";
import IconButton from "components/common/IconButton";
import MenuItem from "components/common/MenuItem";
import SpaceGuestBadge from "./SpaceGuestBadge";
import useToggle from "hooks/useToggle";
import SpaceAddEditModal from "./SpaceAddEditModal";
import useGetUserProfile from "hooks/useGetUserProfile";
import { authActions } from "redux/Ldb/actions/authActions";
import * as UrlConstants from "constants/urls";
import { useSpaceGuestBrands } from "hooks/spaceHooks";
import { getFromStorage, saveToStorage } from "helpers";
import { Tooltip } from "react-tooltip";

type SpaceListHeaderProps = {
    isGuest: boolean;
    handleChangeArchived: (value: boolean) => void;
    handleChangeSortDirection: (value: "asc" | "desc") => void;
    handleChangeAll: () => void;
};

export default function SpaceListHeader(props: SpaceListHeaderProps) {
    const { isGuest, handleChangeArchived, handleChangeSortDirection, handleChangeAll } = props;
    const navigate = useNavigate();
    const [spaceAddEditModal, toggleSpaceAddEditModal] = useToggle();

    const dispatch = useDispatch();
    const getProfileQuery = useGetUserProfile();
    const profile = getProfileQuery?.[0];
    const userProfile = getFromStorage("ally-user");

    console.log("getProfileQuery", getProfileQuery);

    const getSpaceGuestBrandsQuery = useSpaceGuestBrands({
        skip: !isGuest,
    });

    const brandId = userProfile?.user?.assignedBrandsId?.[0] || userProfile?.user?.profile?._id;

    const selectedBrand = useMemo(() => {
        return getSpaceGuestBrandsQuery?.data?.brands?.find((brand) => brand?._id === brandId);
    }, [getSpaceGuestBrandsQuery?.data?.brands, brandId]);

    const brandName = isGuest
        ? selectedBrand?.brandName
        : (profile?.profile?.businessName as string);
    const avatar = isGuest ? selectedBrand?.avatar : (profile?.profile?.avatar as string);

    const allUnselectedBrands = getSpaceGuestBrandsQuery?.data?.brands?.filter(
        (brand) => brand?._id !== selectedBrand?._id
    );

    const handleLogout = () => {
        dispatch(authActions.logOut());
        if (isGuest) {
            navigate(UrlConstants.SPACES_GUEST_LOGIN);
        } else {
            navigate("/login");
        }
        window.location.reload();
    };

    const handleChangeBrand = (brandId: string) => {
        const userData = { ...userProfile };
        userData.user.assignedBrandsId = [brandId];
        saveToStorage("ally-user", userData, true);
        window.location.replace(UrlConstants.SPACES_GUEST);
    };

    return (
        <>
            <div className="w-full py-[12px] px-[16px] border-b border-slate-100">
                <div className="flex gap-[10px] items-center justify-between">
                    <div className="flex gap-2 items-center w-full">
                        <LogoContainer src={avatar || ""} />

                        <div>
                            <Dropdown
                                dropdownOptions={[
                                    {
                                        label: (
                                            <div className="flex gap-1 items-center">
                                                <LogoContainer src={avatar || ""} />
                                                <p className="text-sm w-fit font-rocGroteskMedium whitespace-nowrap truncate w-full max-w-[100px]">
                                                    {brandName}
                                                </p>
                                            </div>
                                        ),
                                        labelContainerClassName:
                                            "flex !:hover:bg-transparent gap-2 items-center border-b border-slate-100",
                                        value: null,
                                    },
                                    ...(isGuest
                                        ? [
                                              ...(allUnselectedBrands?.length > 0
                                                  ? allUnselectedBrands?.map((brand) => ({
                                                        label: (
                                                            <div className="flex gap-1 items-center">
                                                                <LogoContainer
                                                                    src={brand?.avatar || ""}
                                                                />
                                                                <p className="text-sm font-rocGroteskMedium whitespace-nowrap truncate w-[115px]">
                                                                    {brand?.brandName}
                                                                </p>
                                                            </div>
                                                        ),
                                                        labelContainerClassName:
                                                            "flex !:hover:bg-transparent gap-2 items-center border-b border-slate-100",
                                                        action: () => {
                                                            handleChangeBrand(brand?._id);
                                                        },
                                                        value: null,
                                                    }))
                                                  : []),
                                              {
                                                  label: <MenuItem>Logout</MenuItem>,
                                                  value: null,
                                                  action: handleLogout,
                                              },
                                          ]
                                        : [
                                              {
                                                  label: <MenuItem>Logout</MenuItem>,
                                                  value: null,
                                                  action: handleLogout,
                                              },
                                          ]),
                                ]}
                                dropdown={
                                    <button className="flex gap-1 items-center">
                                        <p className="text-sm font-rocGroteskMedium w-[100px] whitespace-nowrap w-fit truncate overflow-hidden text-ellipsis ">
                                            {brandName || "...."}
                                        </p>
                                        <p className="text-sm font-rocGroteskMedium text-slate-500">
                                            Spaces
                                        </p>
                                        <i className="ri-arrow-down-s-line"></i>
                                    </button>
                                }
                                wholeContainerClass="!w-fit max-sm:!w-full rounded-lg"
                                dropdownContainerClasses="shadow-none border border-slate-200 rounded-lg !w-full !max-w-full min-w-full border border-slate-100 rounded !max-h-fit !pt-0"
                            />
                        </div>
                    </div>

                    {isGuest ? (
                        <SpaceGuestBadge />
                    ) : (
                        <div className="flex items-center gap-[8px]">
                            <IconButton
                                color="secondary"
                                id="home-button"
                                onClick={() => {
                                    navigate("/dashboard/home");
                                }}
                            >
                                <i className="ri-home-5-line"></i>
                            </IconButton>

                            <Dropdown
                                name="filter"
                                value={""}
                                handleChange={() => {}}
                                dropdownOptions={[
                                    {
                                        label: <MenuItem>All</MenuItem>,
                                        action: () => {
                                            handleChangeAll();
                                        },
                                        value: null,
                                    },
                                    {
                                        label: <MenuItem>Recent</MenuItem>,
                                        action: () => {
                                            handleChangeSortDirection("desc");
                                        },
                                        value: null,
                                    },
                                    {
                                        label: <MenuItem>Oldest</MenuItem>,
                                        action: () => {
                                            handleChangeSortDirection("asc");
                                        },
                                        value: null,
                                    },
                                    {
                                        label: <MenuItem>Archived</MenuItem>,
                                        action: () => {
                                            handleChangeArchived(true);
                                        },
                                        value: null,
                                    },
                                ]}
                                dropdown={
                                    <IconButton color="secondary" id="filter-button">
                                        <i className="ri-filter-3-line"></i>
                                    </IconButton>
                                }
                                wholeContainerClass="!w-fit max-sm:!w-full rounded-lg"
                                dropdownContainerClasses="shadow-none border border-slate-200 rounded-lg !w-w-[350px] min-w-full border border-slate-100 rounded !max-h-fit !pt-0"
                            />

                            <Dropdown
                                name="filter"
                                value={""}
                                handleChange={() => {}}
                                dropdownOptions={[
                                    ...[
                                        {
                                            name: "Start a Chat with Synth",
                                            description:
                                                "One-on-one conversations between user and Synth",
                                            action: () => {
                                                navigate("/dashboard/flow");
                                            },
                                        },
                                        {
                                            name: "Create a Space",
                                            description:
                                                "Create and name a space for a project or functional workspace",
                                            action: () => {
                                                toggleSpaceAddEditModal();
                                            },
                                        },
                                    ]?.map(({ name, description, ...rest }) => ({
                                        label: (
                                            <div className="w-[250px]">
                                                <p className="text-slate-600 text-[13px]">{name}</p>
                                                <p className="text-slate-500 text-[13px]">
                                                    {description}
                                                </p>
                                            </div>
                                        ),
                                        value: null,
                                        ...rest,
                                    })),
                                ]}
                                dropdown={
                                    <IconButton id="add-button">
                                        <i className="ri-add-line"></i>
                                    </IconButton>
                                }
                                wholeContainerClass="!w-fit max-sm:!w-full rounded-lg"
                                dropdownContainerClasses="shadow-none border border-slate-200 rounded-lg !w-full !max-w-full min-w-full border border-slate-100 rounded  !pt-0"
                            />
                        </div>
                    )}
                </div>

                {spaceAddEditModal && (
                    <SpaceAddEditModal
                        open={spaceAddEditModal}
                        closeModal={() => {
                            toggleSpaceAddEditModal();
                        }}
                    />
                )}
            </div>

            <Tooltip
                anchorSelect={`#add-button`}
                place="top"
                content="Chat with Synth 1:1 or start a new space"
                style={{
                    width: "290px",
                    zIndex: 9999,
                    backgroundColor: "#142837",
                    color: "#fff",
                    fontSize: "13px",
                    fontWeight: "400 !important",
                    borderRadius: "8px",
                }}
            />

            <Tooltip
                anchorSelect={`#home-button`}
                place="top"
                content="Go back to Home"
                style={{
                    width: "290px",
                    zIndex: 9999,
                    backgroundColor: "#142837",
                    color: "#fff",
                    fontSize: "13px",
                    fontWeight: "400 !important",
                    borderRadius: "8px",
                }}
            />

            <Tooltip
                anchorSelect={`#filter-button`}
                place="top"
                content="Filter and narrow down your spaces"
                style={{
                    width: "290px",
                    zIndex: 9999,
                    backgroundColor: "#142837",
                    color: "#fff",
                    fontSize: "13px",
                    fontWeight: "400 !important",
                    borderRadius: "8px",
                }}
            />
        </>
    );
}

const LogoContainer = ({ src }) => {
    return (
        <div className="w-[24px] h-[24px] border border-slate-200 rounded-lg p-[2px] flex items-center justify-center">
            <img src={src || ""} alt="" className="w-full h-full rounded-md object-cover" />
        </div>
    );
};
